import { useRef, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { CheckCircle2, AlertCircle, Lock, Plus, PlusCircle } from "lucide-react"
import { api } from "@/app/api/client"
import { useClientTheme } from "@/app/components/ThemeContext"
import { PageHeader } from "@/app/components/PageHeader"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import type { ClientTheme } from "@/app/lib/themes"
import { normalizeStr } from "@/app/lib/str"
import { primaryButton } from "@/app/lib/buttonStyles"
import {
  buildBatchPayload,
  deriveEffectivePosition,
  isRowEmpty,
  mapBatchErrorsToRows,
  updateRow,
  type BatchError,
  type BatchTxPayload,
  type EffectivePosition,
} from "@/app/lib/batchTransactions"
import { SegmentedControl } from "@/app/components/FilterBar/SegmentedControl"
import { makeEmptyRow, type InterestKind, type RowState } from "./types"
import { useStrOptions } from "./useStrOptions"
import { entryRowColSpan, TransactionEntryRow } from "./TransactionEntryRow"

// api.post throws `new Error(await res.text())` on non-2xx, so a 422's JSON body
// arrives as the Error message (a JSON string). Parse it back into BatchErrors.
function parseBatchErrors(err: unknown): BatchError[] | null {
  if (!(err instanceof Error)) return null
  try {
    const parsed = JSON.parse(err.message)
    if (Array.isArray(parsed?.errors)) return parsed.errors as BatchError[]
  } catch {
    // not JSON
  }
  return null
}

export function NewTransactionPage() {
  const { theme } = useClientTheme()
  const { canWrite, isLoading: userLoading } = useCurrentUser()
  const queryClient = useQueryClient()

  const strOptions = useStrOptions(canWrite)
  const [kind, setKind] = useState<InterestKind>("leasehold")
  const [rows, setRows] = useState<RowState[]>(() => Array.from({ length: 5 }, makeEmptyRow))
  const [rowErrors, setRowErrors] = useState<Record<string, string[]>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const submittedIdsRef = useRef<string[]>([])

  const patch = (id: string, p: Partial<RowState>) => setRows((rs) => updateRow(rs, id, p))
  const selectStr = (id: string, str: string) =>
    setRows((rs) => updateRow(rs, id, { str, county: strOptions.countyForStr(str) }))
  const addRow = () => setRows((rs) => [...rs, makeEmptyRow()])
  const removeRow = (id: string) =>
    setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.id !== id) : rs))

  const effectivePositionFor = (r: RowState): EffectivePosition => {
    const base = strOptions.inventoryByStr.get(normalizeStr(r.str) ?? "") ?? null
    return deriveEffectivePosition(base, rows, r.id, kind)
  }

  const mutation = useMutation({
    mutationFn: (payload: { transactions: BatchTxPayload[] }) =>
      api.post<{ created: unknown[] }>("/transactions/batch", payload),
    onSuccess: () => {
      for (const k of ["inventories", "orri_inventories", "transactions"])
        queryClient.invalidateQueries({ queryKey: [k] })
      setRows(Array.from({ length: 5 }, makeEmptyRow))
      setRowErrors({})
      setFormError(null)
    },
    onError: (err) => {
      const parsed = parseBatchErrors(err)
      if (parsed) {
        const byRow = mapBatchErrorsToRows(parsed, submittedIdsRef.current)
        setRowErrors(byRow)
        // Auto-expand rows with errors so detail-field issues are visible.
        setRows((rs) => rs.map((r) => (byRow[r.id] ? { ...r, expanded: true } : r)))
        // Surface any non-row-indexed (index:null) messages at form level.
        const formLvl = parsed.filter((e) => e.index == null).flatMap((e) => e.messages)
        setFormError(formLvl.length ? formLvl.join(", ") : null)
      } else {
        setFormError("Something went wrong.")
      }
    },
  })

  // Fields differ between kinds, so carrying rows across the tab switch would
  // be misleading — confirm when anything was entered, then reset the grid.
  // Blocked while a save is in flight: resetting mid-save would land the
  // mutation's feedback on the wrong tab with dead row ids.
  const switchKind = (next: InterestKind) => {
    if (next === kind || mutation.isPending) return
    // isRowEmpty ignores interest_type, so a status-only selection is
    // intentionally not "dirty" — consistent with submit skipping such rows.
    const dirty = rows.some((r) => !isRowEmpty(r))
    if (dirty && !window.confirm("Switching interest type clears the grid. Continue?")) return
    setKind(next)
    setRows(Array.from({ length: 5 }, makeEmptyRow))
    setRowErrors({})
    setFormError(null)
    mutation.reset()
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)
    setRowErrors({})
    mutation.reset()

    const preErrors: Record<string, string[]> = {}
    for (const r of rows) {
      if (isRowEmpty(r)) continue
      const msgs: string[] = []
      if (!normalizeStr(r.str)) msgs.push("Valid STR is required.")
      const artiDiv = r.mode === "DIV" && r.arti
      if (!artiDiv && (r.net_acres.trim() === "" || Number.isNaN(Number(r.net_acres))))
        msgs.push("Net Acres must be a number.")
      if (kind === "leasehold") {
        const nri = r.mode === "ACQ" ? r.nri_percent : r.nri_assigned_percent
        if (nri.trim() === "" || Number.isNaN(Number(nri))) msgs.push("NRI must be a number.")
      } else if (r.mode === "ACQ") {
        if (!r.interest_type) msgs.push("Interest Status is required for mineral acquisitions.")
        else if (
          r.interest_type !== "Open" &&
          (r.royalty.trim() === "" || Number.isNaN(Number(r.royalty)))
        )
          msgs.push("Royalty must be a number.")
      }
      if (r.mode === "DIV" && !effectivePositionFor(r).hasPosition)
        msgs.push("No position held in this STR.")
      if (msgs.length) preErrors[r.id] = msgs
    }
    if (Object.keys(preErrors).length) {
      setRowErrors(preErrors)
      return
    }

    const { payload, submittedRowIds } = buildBatchPayload(rows, effectivePositionFor, kind)
    if (payload.transactions.length === 0) {
      setFormError("Enter at least one transaction.")
      return
    }
    submittedIdsRef.current = submittedRowIds
    mutation.mutate(payload)
  }

  // ── Access gate (defense in depth; nav already hides this page) ────────────
  if (userLoading) {
    return (
      <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
        <div
          className="max-w-[1000px]"
          style={{ color: theme.textMuted, fontFamily: "var(--font-ui)" }}
        >
          Loading…
        </div>
      </div>
    )
  }

  if (!canWrite) {
    return (
      <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
        <div className="max-w-[1000px]">
          <PageHeader
            title="New Transaction"
            subtitle="Record an acquisition or divestiture"
            theme={theme}
            icon={<PlusCircle size={17} strokeWidth={1.75} />}
          />
          <div
            className="rounded-2xl p-8 flex items-center gap-3"
            style={{ backgroundColor: theme.bgSurface, border: `1px solid ${theme.borderColor}` }}
          >
            <Lock size={18} style={{ color: theme.textDim }} />
            <div>
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: theme.textPrimary,
                }}
              >
                Read-only access
              </div>
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: "13px",
                  color: theme.textMuted,
                  marginTop: "2px",
                }}
              >
                Your role does not permit creating transactions. Contact an owner or admin.
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const showError = formError || mutation.isError

  return (
    <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
      <div className="max-w-[1200px]">
        <PageHeader
          title="New Transaction"
          subtitle="Record one or more acquisitions and divestitures"
          theme={theme}
          icon={<PlusCircle size={17} strokeWidth={1.75} />}
        />

        <div className="mb-4 flex items-center justify-between gap-3">
          <SegmentedControl
            value={kind}
            ariaLabel="Interest kind"
            accents={theme.kindAccents}
            onChange={(next) => switchKind(next as InterestKind)}
            options={[
              { label: "Leasehold", value: "leasehold" },
              { label: "Mineral", value: "mineral" },
            ]}
            theme={theme}
          />
        </div>

        <form onSubmit={handleSubmit}>
          <div
            className="rounded-lg p-4 sm:p-6"
            style={{ backgroundColor: theme.bgSurface, border: `1px solid ${theme.borderColor}` }}
          >
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", minWidth: 980, borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <HeaderCell theme={theme} />
                    <HeaderCell theme={theme}>Type</HeaderCell>
                    <HeaderCell theme={theme}>Date</HeaderCell>
                    <HeaderCell theme={theme}>STR</HeaderCell>
                    <HeaderCell theme={theme}>County</HeaderCell>
                    <HeaderCell theme={theme}>Net Acres</HeaderCell>
                    {kind === "leasehold" ? (
                      <>
                        <HeaderCell theme={theme}>NRI %</HeaderCell>
                        <HeaderCell theme={theme}>ORRI (640)</HeaderCell>
                        <HeaderCell theme={theme}>NA Owned</HeaderCell>
                        <HeaderCell theme={theme}>NRI Owned</HeaderCell>
                        <HeaderCell theme={theme}>ORRI %</HeaderCell>
                      </>
                    ) : (
                      <>
                        <HeaderCell theme={theme}>Royalty</HeaderCell>
                        <HeaderCell theme={theme}>NA Owned</HeaderCell>
                      </>
                    )}
                    <HeaderCell theme={theme} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <TransactionEntryRow
                      key={row.id}
                      row={row}
                      rows={rows}
                      kind={kind}
                      strOptions={strOptions}
                      errors={rowErrors[row.id]}
                      onPatch={(p) => patch(row.id, p)}
                      onSelectStr={(s) => selectStr(row.id, s)}
                      onRemove={() => removeRow(row.id)}
                      theme={theme}
                    />
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={entryRowColSpan(kind)} style={{ paddingTop: "12px" }}>
                      <button
                        type="button"
                        onClick={addRow}
                        className="btn flex items-center gap-1.5"
                        style={{
                          fontFamily: "var(--font-ui)",
                          fontSize: "13px",
                          fontWeight: 500,
                          padding: "8px 14px",
                          borderRadius: "8px",
                          backgroundColor: theme.bgSurface2,
                          color: theme.textPrimary,
                          border: `1px solid ${theme.borderColor}`,
                          cursor: "pointer",
                        }}
                      >
                        <Plus size={15} style={{ color: theme.textMuted }} />
                        Add row
                      </button>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <p
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: "12px",
              color: theme.textMuted,
              marginTop: "8px",
            }}
          >
            Entering many rows from a spreadsheet? Use the Import page.
          </p>

          {/* Feedback */}
          {showError && (
            <div
              className="mt-4 rounded-xl p-3 flex items-center gap-2"
              style={{
                backgroundColor: `${theme.brandAccent}12`,
                border: `1px solid ${theme.brandAccent}40`,
              }}
            >
              <AlertCircle size={15} style={{ color: theme.brandAccent }} />
              <span
                style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.brandAccent }}
              >
                {formError ?? "Some rows could not be saved. See the highlighted rows above."}
              </span>
            </div>
          )}
          {mutation.isSuccess && (
            <div
              className="mt-4 rounded-xl p-3 flex items-center gap-2"
              style={{
                backgroundColor: `${theme.brandTeal}12`,
                border: `1px solid ${theme.brandTeal}40`,
              }}
            >
              <CheckCircle2 size={15} style={{ color: theme.brandTeal }} />
              <span
                style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.textPrimary }}
              >
                Transaction(s) logged. The grid has been reset.
              </span>
            </div>
          )}

          <div className="mt-5 flex sm:justify-end">
            <button
              type="submit"
              disabled={!strOptions.ready || mutation.isPending}
              className="btn w-full sm:w-auto"
              style={{
                ...primaryButton(theme),
                justifyContent: "center",
                opacity: !strOptions.ready || mutation.isPending ? 0.6 : 1,
                cursor: !strOptions.ready || mutation.isPending ? "not-allowed" : "pointer",
              }}
            >
              {mutation.isPending ? "Saving…" : "Log Transaction(s)"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Small presentational helpers ─────────────────────────────────────────────

// Column header styled like the app's other dim uppercase labels.
function HeaderCell({ theme, children }: { theme: ClientTheme; children?: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: "4px 6px 10px 6px",
        fontFamily: "var(--font-ui)",
        fontSize: "11px",
        fontWeight: 600,
        color: theme.textDim,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        whiteSpace: "nowrap",
        borderBottom: `1px solid ${theme.borderColor}`,
      }}
    >
      {children}
    </th>
  )
}
